import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { orderService, shippingService } from "../services/dataService";
import { Firm, ShipmentDetail, ShipmentLine, ShipmentUpsert, ShippableOrder } from "../types";
import { todayLocal } from "../utils/dates";
import { poDate } from "../utils/purchasing";
import { EPS, normalizePlate, parseQty, plateValid, postalValid, qtyText, shDriverNames, shErr, shFmtDate, shFmtQty, shLayerBlocked } from "../utils/shipping";
import { usePermissions } from "../context/PermissionContext";
import ConfirmModal from "./ConfirmModal";
import { FirmPickerField } from "./FirmPicker";
import OrderPickerModal, { dueState } from "./OrderPicker";
import "../pages/PurchaseOrders.css";
import "../pages/Shipping.css";

/**
 * Sevkiyat modalı — yeni sevkiyat ve taslak düzenleme. Sayfaya gidilmez; liste arkada kalır.
 * Üç sabit bölge: başlık · kaydırılan belge gövdesi (sevkiyat, siparişler, taşıma, teslimat adresi, not) · toplamlar + eylemler.
 * Siparişler OrderPicker ile eklenir; kalan miktar aşılamaz. Modal /ship çağırmaz: "Kaydet ve sevk et" kaydeder, sevk penceresini sayfa açar.
 */
export interface ShipmentModalProps {
  /** Düzenlenen taslak; null = yeni sevkiyat. */
  shipment: ShipmentDetail | null;
  /** /shipping/new?firmId=&orderId= ön seçimi (Sipariş / Fatura Yönetimi ekranlarından gelir). */
  initialFirmId?: string;
  initialOrderIds?: string[];
  firms: Firm[];
  escDisabled?: boolean;
  /** e-İrsaliye açık: irsaliye numarasını sevkte sistem verir; plaka, şoför/taşıyıcı ve posta kodu sevkte zorunlu. */
  eDespatch?: boolean;
  /** Kirli form koruması modalde çözülür, sonra çağrılır. */
  onClose: () => void;
  onSaved: (r: { shipmentId: string; number?: string; thenShip: boolean }) => void;
}

interface Head {
  firmId: string; shipmentDate: string; carrier: string; vehiclePlate: string; driverFirstName: string; driverLastName: string; driverTckn: string;
  trailerPlate: string; plateIsForeign: "" | "1"; carrierTaxNumber: string;
  trackingNo: string; packageCount: string; grossWeightKg: string; deliveryTitle: string; deliveryAddress: string;
  deliveryDistrict: string; deliveryCity: string; deliveryPostalCode: string; deliveryContact: string; deliveryPhone: string; waybillNo: string; waybillDate: string; note: string;
}
type UndoSnap = { added: string[]; qty: Record<string, string>; pk: Record<string, string>; touched: string[]; msg: string };

const emptyHead = (): Head => ({
  firmId: "", shipmentDate: todayLocal(), carrier: "", vehiclePlate: "", driverFirstName: "", driverLastName: "", driverTckn: "",
  trailerPlate: "", plateIsForeign: "", carrierTaxNumber: "", trackingNo: "", packageCount: "",
  grossWeightKg: "", deliveryTitle: "", deliveryAddress: "", deliveryDistrict: "", deliveryCity: "", deliveryPostalCode: "", deliveryContact: "", deliveryPhone: "",
  waybillNo: "", waybillDate: "", note: "",
});
const headOf = (d: ShipmentDetail): Head => {
  const [first, last] = shDriverNames(d);
  return {
    firmId: d.firmId, shipmentDate: d.shipmentDate.slice(0, 10), carrier: d.carrier || "", vehiclePlate: d.vehiclePlate || "",
    driverFirstName: first, driverLastName: last, driverTckn: d.driverTckn || "",
    trailerPlate: d.trailerPlate || "", plateIsForeign: d.plateIsForeign ? "1" : "", carrierTaxNumber: d.carrierTaxNumber || "",
    trackingNo: d.trackingNo || "",
    packageCount: d.packageCount != null ? qtyText(d.packageCount) : "", grossWeightKg: d.grossWeightKg != null ? qtyText(d.grossWeightKg) : "",
    deliveryTitle: d.deliveryTitle || "", deliveryAddress: d.deliveryAddress || "", deliveryDistrict: d.deliveryDistrict || "",
    deliveryCity: d.deliveryCity || "", deliveryPostalCode: d.deliveryPostalCode || "", deliveryContact: d.deliveryContact || "", deliveryPhone: d.deliveryPhone || "",
    waybillNo: d.waybillNo || "", waybillDate: d.waybillDate ? d.waybillDate.slice(0, 10) : "", note: d.note || "",
  };
};
const snapshotOf = (head: Head, added: string[], qty: Record<string, string>, pk: Record<string, string>) =>
  JSON.stringify({ head, added, qty: added.map((id) => qty[id] ?? ""), pk: added.map((id) => pk[id] ?? "") });
const nz = (s: string) => s.trim() || null;
const INT = /^\d+$/;

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>,
};

const Field: React.FC<{ label: string; hint?: string; span?: 2 | 3; as?: "label" | "div"; children: React.ReactNode }> = ({ label, hint, span, as = "label", children }) => {
  const Tag = as as any;
  return <Tag className={`po-field ${span ? `span${span}` : ""}`}><span>{label}{hint && <em>{hint}</em>}</span>{children}</Tag>;
};

const ShipmentModal: React.FC<ShipmentModalProps> = ({ shipment, initialFirmId, initialOrderIds, firms, escDisabled, eDespatch, onClose, onSaved }) => {
  const { has, hasModule } = usePermissions();
  const canShip = has("shipping:update");

  // ---- Form durumu (ilk değerler: taslak ya da boş sevkiyat) ----
  const [head, setHead] = useState<Head>(() => (shipment ? headOf(shipment) : { ...emptyHead(), firmId: initialFirmId ?? "" }));
  const [added, setAdded] = useState<string[]>(() => shipment?.lines.map((l) => l.orderId) ?? []); // sevkiyata eklenen sipariş kimlikleri (satır sırası)
  // Kayıtlı satırlar forma eklenmiş sayılır; miktarları kullanıcı kararıdır ("otomatik" çipi almaz)
  const [touched, setTouched] = useState<Set<string>>(() => new Set(shipment?.lines.map((l) => l.orderId) ?? []));
  const [qty, setQty] = useState<Record<string, string>>(() => Object.fromEntries((shipment?.lines ?? []).map((l) => [l.orderId, qtyText(l.quantity)])));
  const [pk, setPk] = useState<Record<string, string>>(() => Object.fromEntries((shipment?.lines ?? []).map((l) => [l.orderId, l.packageCount != null ? qtyText(l.packageCount) : ""])));
  // Kirli form tabanı state'te durur: derin bağlantı tohumu sonrası yeniden alınınca ekran da tazelenir
  const [initialJson, setInitialJson] = useState(() => snapshotOf(head, added, qty, pk));
  const [seededAt, setSeededAt] = useState(0);

  const [orders, setOrders] = useState<ShippableOrder[]>([]);
  const [ordersFirm, setOrdersFirm] = useState(""); // listenin başarıyla yüklendiği firma
  // Eklenen siparişin son bilinen hâli: liste yeniden çekilince ya da çekilemeyince satır ekranda kalır
  const [snapOrders, setSnapOrders] = useState<Record<string, ShippableOrder>>({});
  const [ordersLoading, setOrdersLoading] = useState(() => !!head.firmId);
  const [ordersError, setOrdersError] = useState("");

  const [error, setError] = useState("");
  const [badId, setBadId] = useState<string | null>(null);
  const [info, setInfo] = useState("");
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState<null | "draft" | "ship">(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [undoSnap, setUndoSnap] = useState<UndoSnap | null>(null);
  const [pendingFirm, setPendingFirm] = useState<Firm | null | undefined>(undefined);
  const [guardOpen, setGuardOpen] = useState(false);
  const [focusTarget, setFocusTarget] = useState<string | null>(null); // yeni satır çizildikten sonra miktara odaklan

  const seeded = useRef(false);
  const ordersGen = useRef(0);
  const justApplied = useRef(false); // Uygula sonrası odak yeni satıra gider, "Sipariş ekle" düğmesine değil
  const openFocus = useRef(true);
  const overlayRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const qtyRefs = useRef(new Map<string, HTMLInputElement>());
  const pkRefs = useRef(new Map<string, HTMLInputElement>());
  const createdRef = useRef<{ id: string; number?: string } | null>(null);

  const firm = useMemo(() => firms.find((f) => f.firmId === head.firmId), [firms, head.firmId]);

  const snapshot = () => snapshotOf(head, added, qty, pk);
  const dirty = snapshot() !== initialJson;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // Açan öğe ilk render'da (FirmPickerField autoFocus odağı almadan) yakalanır; kapanışta odak ona döner
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  // StrictMode geliştirmede yukarıdaki temizliği bir kez erken çalıştırır; odak modal dışına kaçtıysa firma alanına döner
  useEffect(() => {
    if (!head.firmId && !overlayRef.current?.contains(document.activeElement)) overlayRef.current?.querySelector<HTMLElement>(".fp-control")?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Yalnız ?orderId= geldiyse firma siparişten çözülür
  useEffect(() => {
    if (shipment || initialFirmId || !initialOrderIds?.length) return;
    orderService.getById(initialOrderIds[0])
      .then((o) => {
        if (seeded.current) return; // kullanıcı bu arada firmayı kendisi seçti
        if (!o.firmId) { setError("Bağlantıdaki sipariş bulunamadı; firmayı seçip siparişi ekleyin."); return; }
        setHead((h) => ({ ...h, firmId: o.firmId }));
        if (!dirtyRef.current) setSeededAt((n) => n + 1); // kullanıcı bu arada yazdıysa yazdıkları "kaydedilmiş" sayılmaz
      })
      .catch(() => { if (!seeded.current) setError("Bağlantıdaki sipariş bulunamadı; firmayı seçip siparişi ekleyin."); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kaydedilmemiş değişiklik varken sekme kapatma/yenileme uyarısı
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  // ---- Siparişler ----
  /** Firmanın sevk edilebilir siparişleri; düzenlemede bu sevkiyatın kendi miktarı kalana geri eklenir. */
  const loadOrders = useCallback(async () => {
    const firmId = head.firmId;
    const gen = ++ordersGen.current; // yavaş kalan eski firmanın yanıtı yeni listeyi ezmesin
    if (!firmId) { setOrders([]); setOrdersFirm(""); setOrdersLoading(false); setOrdersError(""); return; }
    setOrdersLoading(true); setOrdersError("");
    try {
      let list = await shippingService.shippableOrders(firmId, !!shipment);
      if (gen !== ordersGen.current) return;
      if (shipment) {
        const own = new Map(shipment.lines.map((l) => [l.orderId, l.quantity]));
        list = list.map((o) => (own.has(o.orderId)
          ? { ...o, remaining: o.remaining + (own.get(o.orderId) || 0), shipped: Math.max(0, o.shipped - (own.get(o.orderId) || 0)) } // bu taslağın kendi miktarı "sevk edilmiş" sayılmaz
          : o))
          .filter((o) => o.remaining > EPS || own.has(o.orderId));
      }
      setOrders(list); setOrdersFirm(firmId);
      setSnapOrders((m) => { const n = { ...m }; list.forEach((o) => { if (n[o.orderId]) n[o.orderId] = o; }); return n; });
    } catch {
      if (gen !== ordersGen.current) return;
      setOrdersError("Siparişler yüklenemedi."); // eklenen satırlar ekranda kalır (anlık görüntüden)
    } finally { if (gen === ordersGen.current) setOrdersLoading(false); }
  }, [head.firmId, shipment]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Derin bağlantıdaki sipariş(ler) liste gelince BİR kez satır olarak eklenir; seçici açılmaz
  useEffect(() => {
    if (shipment || seeded.current || !head.firmId || ordersLoading || ordersFirm !== head.firmId) return;
    seeded.current = true;
    const ids = Array.from(new Set(initialOrderIds ?? []));
    if (!ids.length) return;
    const ok = ids.filter((x) => orders.some((o) => o.orderId === x));
    if (ok.length) {
      const picked = ok.map((x) => orders.find((o) => o.orderId === x)!);
      setAdded(ok);
      setQty(Object.fromEntries(picked.map((o) => [o.orderId, qtyText(o.remaining)])));
      setPk(Object.fromEntries(ok.map((x) => [x, ""])));
      setSnapOrders((m) => { const n = { ...m }; picked.forEach((o) => { n[o.orderId] = o; }); return n; });
      setFocusTarget(ok[0]);
    }
    const miss = ids.length - ok.length;
    if (miss > 0) {
      setInfo(miss === 1 && ids.length === 1
        ? "Bağlantıdaki sipariş sevkiyata eklenemedi: iptal edilmiş, pasife alınmış ya da sevk edilecek kalanı yok."
        : `${miss} sipariş eklenemedi: sevk edilebilir değil ya da başka firmaya ait.`);
    }
    // Tohum gecikirse (liste hatası → Yeniden dene) kullanıcı bu arada alan doldurmuş olabilir: o zaman taban yenilenmez, kapanışta uyarı çıkar
    if (!dirty) setSeededAt((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment, orders, ordersLoading, ordersFirm, head.firmId, initialOrderIds]);

  // Ön seçim (firma çözümü / satır tohumu) bitince form temiz sayılır; boyamadan önce alınır, kirli rozeti yanıp sönmez
  useLayoutEffect(() => {
    if (seededAt) setInitialJson(snapshot());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seededAt]);

  // ---- Satırlar ----
  const lines = useMemo(
    () => added.map((x) => orders.find((o) => o.orderId === x) ?? snapOrders[x]).filter((o): o is ShippableOrder => !!o),
    [added, orders, snapOrders]);
  /** Kayıtlı ama listede olmayan satırlar (sipariş iptal/pasif) — yalnız düzenleme kipinde, liste bu firma için gelmişken doğar. */
  const orphanLines = useMemo(
    () => (ordersFirm !== head.firmId || (ordersLoading && orders.length === 0) ? [] : added)
      .filter((x) => !orders.some((o) => o.orderId === x) && !snapOrders[x])
      .map((x) => shipment?.lines.find((l) => l.orderId === x))
      .filter((l): l is ShipmentLine => !!l),
    [added, orders, snapOrders, shipment, ordersLoading, ordersFirm, head.firmId]);
  const rowCount = lines.length + orphanLines.length;
  /** Birim başına toplam — farklı birimler birbirine EKLENMEZ. */
  const totals = useMemo(() => {
    const m = new Map<string, number>();
    lines.forEach((o) => { const p = parseQty(qty[o.orderId] ?? ""); if (p.ok) m.set(o.unit, (m.get(o.unit) ?? 0) + p.value); });
    return Array.from(m.entries()).map(([unit, q]) => `${shFmtQty(q)} ${unit}`);
  }, [lines, qty]);
  const pkTotal = useMemo(() => lines.reduce((s, o) => s + (parseInt(pk[o.orderId] || "", 10) || 0), 0), [lines, pk]);
  const dirtyCount = useMemo(() => lines.filter((o) => (qty[o.orderId] ?? "") !== qtyText(o.remaining)).length, [lines, qty]);
  const canAdd = !!head.firmId && !ordersLoading && !(ordersError && orders.length === 0);

  // Eklenen ilk yeni satırın miktar girdisine odaklan (satır çizildikten sonra)
  useEffect(() => {
    if (!focusTarget) return;
    if (shLayerBlocked(overlayRef.current)) { setFocusTarget(null); return; } // açık seçicinin arkasındaki girdiye odak verilmez
    const el = qtyRefs.current.get(focusTarget);
    if (el) { el.focus(); el.select(); setFocusTarget(null); }
  }, [focusTarget, lines]);

  // Açılış odağı sipariş listesi gelince verilir: düzenlemede ilk satırın sevk miktarı, ön seçili firmada "Sipariş ekle".
  // Firma yoksa FirmPickerField autoFocus alır; kullanıcı bu arada modalde bir alana geçtiyse odak çalınmaz.
  useEffect(() => {
    if (!openFocus.current || !head.firmId || ordersLoading || focusTarget || (ordersFirm !== head.firmId && !ordersError)) return;
    openFocus.current = false;
    if (overlayRef.current?.contains(document.activeElement) || shLayerBlocked(overlayRef.current)) return;
    const first = lines[0] ? qtyRefs.current.get(lines[0].orderId) : undefined;
    (first ?? addBtnRef.current)?.focus();
  }, [head.firmId, ordersLoading, ordersFirm, ordersError, focusTarget, lines]);

  const set = (k: keyof Head, v: string) => { setHead((h) => ({ ...h, [k]: v })); setError(""); };

  const applyFirm = (f: Firm | null) => {
    set("firmId", f?.firmId || "");
    setAdded([]); setQty({}); setPk({}); setTouched(new Set()); setUndoSnap(null); setBadId(null); setSnapOrders({}); setOrders([]); seeded.current = true;
  };

  // Firma değişikliği onayı: odak onayın "Vazgeç"ine geçer (seçicinin alana geri odaklaması bittikten sonra), kapanınca Firma alanına döner
  const askFirmChange = (f: Firm | null) => {
    setPendingFirm(f);
    window.setTimeout(() => document.querySelector<HTMLElement>(".confirm-modal-actions .btn-secondary")?.focus(), 30);
  };
  const closeFirmChange = () => {
    setPendingFirm(undefined);
    window.setTimeout(() => overlayRef.current?.querySelector<HTMLElement>(".fp-control")?.focus(), 0);
  };

  const applyFirmAddress = () => {
    if (!firm) return;
    setHead((h) => ({
      ...h,
      deliveryTitle: firm.firmName, deliveryAddress: firm.address || "",
      deliveryDistrict: firm.district?.districtName || "", deliveryCity: firm.city?.cityName || "", deliveryPostalCode: firm.postalCode || "",
      deliveryContact: firm.contactPerson || "", deliveryPhone: firm.phone || "",
    }));
  };

  const snap = (msg: string) => setUndoSnap({ added, qty, pk, touched: Array.from(touched), msg });
  /** Yalnız çıkarılan satırları geri getirir; kalan satırlarda sonradan yazılan miktarlar korunur. */
  const undo = () => {
    if (!undoSnap) return;
    setAdded(undoSnap.added);
    const back = undoSnap.added.filter((x) => !added.includes(x));
    setQty((m) => { const n = { ...m }; back.forEach((x) => { n[x] = undoSnap.qty[x] ?? ""; }); return n; });
    setPk((m) => { const n = { ...m }; back.forEach((x) => { n[x] = undoSnap.pk[x] ?? ""; }); return n; });
    setTouched((t) => { const n = new Set(t); back.forEach((x) => { if (undoSnap.touched.includes(x)) n.add(x); else n.delete(x); }); return n; });
    setUndoSnap(null); setError(""); setBadId(null);
  };

  const applyOrders = (ids: string[], picked: ShippableOrder[]) => {
    const removed = added.filter((x) => !ids.includes(x));
    if (removed.length) {
      const saved = removed.filter((x) => shipment?.lines.some((l) => l.orderId === x)).length;
      snap(saved > 0
        ? `Kayıtlı ${saved} satır çıkarıldı; taslak kaydedilmeden kalıcı olmaz.`
        : `${removed.length} sipariş sevkiyattan çıkarıldı.`);
    } else setUndoSnap(null);

    setAdded(ids);
    setSnapOrders((m) => { const n = { ...m }; picked.forEach((o) => { n[o.orderId] = o; }); return n; });
    setQty((m) => { // yalnız EKSİK olanlar doldurulur: elle yazılmış miktar korunur
      const n = { ...m };
      picked.forEach((o) => { if (n[o.orderId] === undefined) n[o.orderId] = o.remaining > EPS ? qtyText(o.remaining) : ""; });
      return n;
    });
    setPk((m) => { const n = { ...m }; ids.forEach((x) => { if (n[x] === undefined) n[x] = ""; }); return n; });
    setError(""); setBadId(null);

    const firstNew = ids.find((x) => !added.includes(x)) ?? null;
    justApplied.current = !!firstNew; // yeni satır yoksa odak "Sipariş ekle" düğmesine dönsün
    setFocusTarget(firstNew);
  };

  const removeLine = (orderId: string, label: string) => {
    snap(shipment?.lines.some((l) => l.orderId === orderId)
      ? "Kayıtlı 1 satır çıkarıldı; taslak kaydedilmeden kalıcı olmaz."
      : `${label} sevkiyattan çıkarıldı.`);
    setAdded((a) => a.filter((x) => x !== orderId));
    setError(""); setBadId(null);
    if (added.length === 1) window.setTimeout(() => addBtnRef.current?.focus(), 0);
  };

  const fillAllRemaining = () => {
    setQty((m) => { const n = { ...m }; lines.forEach((o) => { n[o.orderId] = qtyText(o.remaining); }); return n; });
    setTouched((t) => { const n = new Set(t); lines.forEach((o) => n.add(o.orderId)); return n; });
    setError(""); setBadId(null);
  };

  const setLineQty = (orderId: string, v: string) => {
    setQty((m) => ({ ...m, [orderId]: v }));
    setTouched((t) => new Set(t).add(orderId));
    setError(""); setBadId(null);
  };
  const setLinePk = (orderId: string, v: string) => { setPk((m) => ({ ...m, [orderId]: v })); setError(""); };

  // ---- Satır içi klavye: Enter ile Sevk → Koli → sonraki satır; Shift+Enter ve ↑↓ aynı sütunda ----
  const lineKey = (e: React.KeyboardEvent<HTMLInputElement>, index: number, col: "ship" | "pk") => {
    const refs = col === "ship" ? qtyRefs.current : pkRefs.current;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      const next = lines[index + (e.key === "ArrowDown" ? 1 : -1)];
      if (next) { e.preventDefault(); refs.get(next.orderId)?.focus(); }
      return;
    }
    if (e.key !== "Enter" || e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    if (e.shiftKey) { const prev = lines[index - 1]; if (prev) refs.get(prev.orderId)?.focus(); return; }
    if (col === "ship") { pkRefs.current.get(lines[index].orderId)?.focus(); return; }
    const next = lines[index + 1];
    if (next) qtyRefs.current.get(next.orderId)?.focus();
    else if (canAdd) setPickerOpen(true);
  };

  // ---- Doğrulama ve kayıt ----
  const showError = (msg: string, focus?: () => void) => {
    setError(msg);
    if (focus) window.setTimeout(focus, 0); else bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };
  const focusInput = (el?: HTMLInputElement | null) => () => { el?.focus(); el?.scrollIntoView({ block: "center" }); };

  const pkHeadBad = head.packageCount.trim() !== "" && !INT.test(head.packageCount.trim());
  // Ağırlık 0 olabilir (sunucu yalnız negatifi reddeder; eski formla 0 kg kaydedilmiş taslaklar var)
  const gwParse = parseQty(head.grossWeightKg);
  const weightBad = head.grossWeightKg.trim() !== "" && !gwParse.ok && gwParse.code !== "zero";
  const tcknBad = head.driverTckn.length > 0 && head.driverTckn.length < 11;
  const postalBad = head.deliveryPostalCode.trim() !== "" && !/^\d{5}$/.test(head.deliveryPostalCode.trim());
  const carrierIdBad = head.carrierTaxNumber.trim() !== "" && !/^\d{10,11}$/.test(head.carrierTaxNumber.trim());
  // e-İrsaliye ipuçları (kaydı engellemez; sevk penceresi zorunlu alanları ayrıca sorar)
  const foreign = head.plateIsForeign === "1";
  const plateHint = eDespatch && head.vehiclePlate.trim() && !plateValid(head.vehiclePlate, foreign) ? "e-İrsaliye bu plakayı kabul etmez: 01–81 il koduyla başlamalı (34ABC123); yabancı plakaysa işaretleyin." : "";
  const trailerHint = eDespatch && head.trailerPlate.trim() && !plateValid(head.trailerPlate, foreign) ? "Dorse plakası e-İrsaliyede geçersiz." : "";
  const postalHint = eDespatch && !postalBad && head.deliveryPostalCode.trim() && !postalValid(head.deliveryPostalCode) ? "Posta kodu 01–81 il koduyla başlamalı." : "";

  const validate = (): boolean => {
    setTried(true); setBadId(null);
    if (!head.firmId) { showError("Firma seçin."); return false; }
    if (!head.shipmentDate) { showError("Sevk tarihi girin."); return false; }
    if (added.length === 0) { showError("Sevkiyata en az bir sipariş ekleyin."); return false; }
    if (added.some((x) => !lines.some((o) => o.orderId === x) && !orphanLines.some((l) => l.orderId === x))) {
      showError("Bazı satırların siparişi okunamadı; listeyi yenileyip tekrar deneyin."); return false;
    }
    if (orphanLines.length > 0) { showError(`${orphanLines[0].modelCode || "Sipariş"}: sipariş listede yok; satırı kaldırın.`); return false; }
    for (const o of lines) {
      const code = o.modelCode || "Sipariş";
      const p = parseQty(qty[o.orderId] ?? "");
      if (!p.ok) {
        setBadId(o.orderId);
        showError(p.code === "empty" ? `${code}: sevk miktarı girin.`
          : p.code === "zero" ? `${code}: sevk miktarı sıfırdan büyük olmalı.`
            : `${code}: sevk miktarı geçersiz. Ondalık için virgül kullanın (örn. 12,5).`, focusInput(qtyRefs.current.get(o.orderId)));
        return false;
      }
      if (p.value > o.remaining + EPS) {
        setBadId(o.orderId);
        showError(`${code}: kalan ${shFmtQty(o.remaining)} ${o.unit} aşılamaz.`, focusInput(qtyRefs.current.get(o.orderId)));
        return false;
      }
      const pkRaw = (pk[o.orderId] || "").trim();
      if (pkRaw && !INT.test(pkRaw)) { showError(`${code}: koli adedi tam sayı olmalı.`, focusInput(pkRefs.current.get(o.orderId))); return false; }
    }
    if (pkHeadBad) { showError("Koli sayısı tam sayı olmalı."); return false; }
    if (weightBad) { showError("Brüt ağırlık geçersiz (örn. 125,5); boş da bırakılabilir."); return false; }
    if (head.driverTckn.length !== 0 && head.driverTckn.length !== 11) { showError("Şoför TCKN 11 haneli rakam olmalı."); return false; }
    if (carrierIdBad) { showError("Taşıyıcı VKN 10, TCKN 11 haneli rakam olmalı."); return false; }
    if (postalBad) { showError("Teslimat posta kodu 5 haneli rakam olmalı."); return false; }
    return true;
  };

  const dto = (): ShipmentUpsert => {
    const gw = parseQty(head.grossWeightKg);
    return {
      firmId: head.firmId, shipmentDate: head.shipmentDate,
      // e-İrsaliye açıkken plaka GİB biçimine çevrilir (Türkçe harf → Latin); kapalıyken yazıldığı gibi
      carrier: nz(head.carrier), vehiclePlate: nz(eDespatch ? normalizePlate(head.vehiclePlate) : head.vehiclePlate),
      // Ad/soyad ayrı gider; sunucu görüntüleme adını (driverName) birleştirir
      driverFirstName: nz(head.driverFirstName), driverLastName: nz(head.driverLastName),
      driverTckn: nz(head.driverTckn), trackingNo: nz(head.trackingNo),
      trailerPlate: nz(eDespatch ? normalizePlate(head.trailerPlate) : head.trailerPlate), plateIsForeign: head.plateIsForeign === "1", carrierTaxNumber: nz(head.carrierTaxNumber),
      packageCount: head.packageCount.trim() ? parseInt(head.packageCount.trim(), 10) : null,
      grossWeightKg: gw.ok ? gw.value : gw.code === "zero" ? 0 : null,
      deliveryTitle: nz(head.deliveryTitle), deliveryAddress: nz(head.deliveryAddress),
      deliveryDistrict: nz(head.deliveryDistrict), deliveryCity: nz(head.deliveryCity), deliveryPostalCode: nz(head.deliveryPostalCode),
      deliveryContact: nz(head.deliveryContact), deliveryPhone: nz(head.deliveryPhone),
      waybillNo: nz(head.waybillNo), waybillDate: head.waybillNo.trim() && head.waybillDate ? head.waybillDate : null,
      note: nz(head.note),
      lines: lines.map((o) => {
        const p = parseQty(qty[o.orderId] ?? "");
        const pkRaw = (pk[o.orderId] || "").trim();
        return { orderId: o.orderId, quantity: p.ok ? p.value : 0, packageCount: pkRaw ? parseInt(pkRaw, 10) : null };
      }),
    };
  };

  const submit = async (thenShip: boolean) => {
    if (busy) return;
    if (!validate()) return;
    setBusy(thenShip ? "ship" : "draft"); setError(""); setInfo("");
    let id = shipment?.shipmentId ?? createdRef.current?.id;
    let number = shipment?.number ?? createdRef.current?.number;
    try {
      if (id) await shippingService.update(id, dto());
      else { const r = await shippingService.create(dto()); id = r.shipmentId; number = r.number; createdRef.current = { id, number }; } // yeniden denemede ikinci sevkiyat açılmasın
    } catch (e) { setBusy(null); showError(shErr(e, "Sevkiyat kaydedilemedi.")); return; }
    onSaved({ shipmentId: id!, number, thenShip });
  };

  const finish = () => (createdRef.current ? onSaved({ shipmentId: createdRef.current.id, number: createdRef.current.number, thenShip: false }) : onClose());
  const requestClose = () => { if (busy) return; if (dirty) setGuardOpen(true); else finish(); };

  // ---- Modal kısayolları: Esc kapat · Ctrl+Enter / Ctrl+S taslak kaydet · F2 sipariş ekle ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (escDisabled || guardOpen || pendingFirm !== undefined || shLayerBlocked(overlayRef.current)) return;
      // stopPropagation şart: aynı tuş basımında yeniden kaydolan dinleyiciler (arkadaki çekmece, açılan kirli-form onayı) Esc'i ikinci kez almasın
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); requestClose(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key === "Enter" || e.key.toLowerCase() === "s")) { e.preventDefault(); submit(false); }
      else if (e.key === "F2") { e.preventDefault(); if (canAdd) setPickerOpen(true); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  });

  const today0 = new Date().setHours(0, 0, 0, 0);
  const addButton = (
    <button type="button" className="po-btn" onClick={() => setPickerOpen(true)}>{I.plus} Sipariş ekle</button>
  );

  return (
    <div className="po-overlay center" ref={overlayRef} onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      <form className="po-modal xl" role="dialog" aria-modal="true" aria-labelledby="sh-om-title" onSubmit={(e) => e.preventDefault()}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">Sevkiyat</span>
            <h3 id="sh-om-title">{shipment ? `Taslağı düzenle · ${shipment.number}` : "Yeni sevkiyat"}</h3>
            <p className="po-form-hint">Taslak olarak kaydedin ya da doğrudan sevk edin.</p>
          </div>
          <div className="po-modal-head-side">
            {dirty && <span className="po-dirty">Kaydedilmemiş değişiklik</span>}
            <button type="button" className="po-ib big" aria-label="Kapat" onClick={requestClose}>{I.close}</button>
          </div>
        </div>

        <div className="po-modal-body" ref={bodyRef}>
          {error && <div className="po-note err" role="alert">{error}</div>}
          {info && <div className="po-note warn">{info}</div>}
          {!hasModule("stock") && <div className="po-note">Stok modülü lisansta olmadığı için sevk yalnız miktar takibi yapar; stok bakiyesi değişmez.</div>}

          {/* A — Sevkiyat */}
          <div className="po-form-sec"><h4>Sevkiyat</h4>
            <div className="po-grid4">
              <Field label="Firma" hint="zorunlu" span={2} as="div">
                <FirmPickerField value={head.firmId} firms={firms} allowCreate={false} className={tried && !head.firmId ? "po-invalid" : ""} autoFocus={!shipment && !head.firmId} placeholder="Firma seçin"
                  onChange={(f) => { const next = f?.firmId || ""; if (next === head.firmId) return; if (added.length > 0) askFirmChange(f); else applyFirm(f); }} />
                {firm && <span className="po-fieldnote">{[firm.contactPerson, firm.phone, firm.city?.cityName].filter(Boolean).join(" · ") || "İletişim bilgisi yok"}</span>}
              </Field>
              <Field label="Sevk tarihi" hint="zorunlu"><input type="date" value={head.shipmentDate} className={tried && !head.shipmentDate ? "is-invalid" : ""} onChange={(e) => set("shipmentDate", e.target.value)} /></Field>
              <Field label="Koli sayısı" hint="isteğe bağlı" as="div">
                <div className="po-money"><input inputMode="numeric" placeholder="0" aria-label="Koli sayısı" value={head.packageCount} className={tried && pkHeadBad ? "is-invalid" : ""} onChange={(e) => set("packageCount", e.target.value)} /><i>koli</i></div>
              </Field>
            </div>
          </div>

          {/* B — Siparişler */}
          <div className="po-form-sec">
            <h4>Siparişler <em>{rowCount}</em>
              <span className="sh-h4-btns">
                {rowCount > 0 && dirtyCount > 0 && <button type="button" className="po-btn small" title="Tüm satırlara kalan miktarı yazar" onClick={fillAllRemaining}>Kalanları yaz</button>}
                <button ref={addBtnRef} type="button" className="po-btn small" aria-haspopup="dialog" disabled={!canAdd} title={head.firmId ? undefined : "Önce firma seçin"} onClick={() => setPickerOpen(true)}>{I.plus} Sipariş ekle <kbd className="po-kbd">F2</kbd></button>
              </span>
            </h4>
            {!head.firmId ? (
              <div className="po-blank small"><b>Önce firma seçin</b>Sevk edilecek siparişler firmaya göre listelenir.</div>
            ) : ordersLoading && orders.length === 0 ? (
              <div className="po-loading"><span className="po-spin" /> Siparişler yükleniyor</div>
            ) : ordersError && rowCount === 0 ? (
              <div className="po-blank small"><b>Siparişler yüklenemedi</b><button type="button" className="po-btn" onClick={loadOrders}>Yeniden dene</button></div>
            ) : rowCount === 0 ? (
              orders.length === 0 ? (
                <div className="po-blank small"><b>Sevk edilebilir sipariş yok</b>Bu firmanın açık siparişi yok ya da tümü sevk edilmiş.</div>
              ) : (
                <div className="po-blank small"><b>Henüz sipariş eklenmedi</b>{firm?.firmName ? `${firm.firmName} için ` : ""}{orders.length} sevk edilebilir sipariş var. Sevkiyata girecekleri "Sipariş ekle" ile seçin.{addButton}</div>
              )
            ) : (
              <div className="po-lines sh-lines">
                <div className="po-lines-head"><span>#</span><span>Sipariş</span><span className="num">Kalan</span><span className="num">Sevk miktarı</span><span className="num">Koli</span><span /></div>
                {lines.map((o, i) => {
                  const code = o.modelCode || "Sipariş";
                  const p = parseQty(qty[o.orderId] ?? "");
                  const over = p.ok && p.value > o.remaining + EPS;
                  const pkRaw = (pk[o.orderId] || "").trim();
                  const due = dueState(o.deadline);
                  const days = o.deadline ? Math.round((poDate(o.deadline).setHours(0, 0, 0, 0) - today0) / 86400000) : null;
                  return (
                    <div className="po-line" key={o.orderId}>
                      <div className="c-no">{i + 1}</div>
                      <div className="c-stock">
                        <button type="button" className="po-stockbtn" title="Siparişleri düzenle" onClick={() => { if (canAdd) setPickerOpen(true); }}><b>{o.modelCode || "Model yok"}</b>{o.invoiceNumber && <span className="po-codepill">{o.invoiceNumber}</span>}</button>
                        <div className="po-hintline">
                          {o.modelName && <>{o.modelName} · </>}Sipariş <b>{shFmtQty(o.quantity)} {o.unit}</b> · sevk {shFmtQty(o.shipped)}
                          {o.deadline && <> · Termin <b>{shFmtDate(o.deadline)}</b>{due === "past" && days !== null && <span className="po-badge err">{-days} gün geç</span>}{due === "soon" && <span className="po-badge warn">{days === 0 ? "bugün" : `${days} gün kaldı`}</span>}</>}
                          {o.orderStatusName && <span className={`po-badge ${o.orderStatusId === 4 ? "ok" : "muted"}`}>{o.orderStatusName}</span>}
                          {!touched.has(o.orderId) && <span className="po-badge muted">otomatik</span>}
                        </div>
                      </div>
                      <div className="c-rem"><b className={over ? "over" : o.remaining <= EPS ? "zero" : ""}>{shFmtQty(o.remaining)}</b><div className="po-sub">{o.unit}</div></div>
                      <div className="c-ship">
                        <div className="po-money">
                          <input ref={(el) => { if (el) qtyRefs.current.set(o.orderId, el); else qtyRefs.current.delete(o.orderId); }} inputMode="decimal" placeholder="0" aria-label={`${code} sevk miktarı`}
                            className={over || badId === o.orderId ? "is-invalid" : ""} value={qty[o.orderId] ?? ""}
                            onFocus={(e) => e.currentTarget.select()} onKeyDown={(e) => lineKey(e, i, "ship")} onChange={(e) => setLineQty(o.orderId, e.target.value)} />
                          <i>{o.unit}</i>
                        </div>
                      </div>
                      <div className="c-pk">
                        <div className="po-money">
                          <input ref={(el) => { if (el) pkRefs.current.set(o.orderId, el); else pkRefs.current.delete(o.orderId); }} inputMode="numeric" placeholder="0" aria-label={`${code} koli`}
                            className={tried && pkRaw && !INT.test(pkRaw) ? "is-invalid" : ""} value={pk[o.orderId] ?? ""}
                            onKeyDown={(e) => lineKey(e, i, "pk")} onChange={(e) => setLinePk(o.orderId, e.target.value)} />
                          <i>koli</i>
                        </div>
                      </div>
                      <div className="c-act"><button type="button" className="po-ib danger" tabIndex={-1} title="Satırı kaldır" aria-label={`${code} satırını kaldır`} onClick={() => removeLine(o.orderId, code)}>{I.close}</button></div>
                    </div>
                  );
                })}
                {orphanLines.map((l, k) => (
                  <div className="po-line" key={l.shipmentLineId}>
                    <div className="c-no">{lines.length + k + 1}</div>
                    <div className="c-stock"><b>{l.modelCode || "Model yok"}</b><div className="po-hintline">{l.modelName && <>{l.modelName} · </>}<span className="po-badge warn">Sipariş listede yok</span></div></div>
                    <div className="c-rem">—</div>
                    <div className="c-ship num"><b>{shFmtQty(l.quantity)} {l.unit}</b></div>
                    <div className="c-pk num">{l.packageCount ?? "—"}</div>
                    <div className="c-act"><button type="button" className="po-ib danger" tabIndex={-1} title="Satırı kaldır" aria-label={`${l.modelCode || "Sipariş"} satırını kaldır`} onClick={() => removeLine(l.orderId, l.modelCode || "Sipariş")}>{I.close}</button></div>
                  </div>
                ))}
              </div>
            )}
            {ordersError && rowCount > 0 && <div className="po-note warn">Sipariş listesi yenilenemedi; satırlar son bilinen hâliyle gösteriliyor. <button type="button" className="po-link" onClick={loadOrders}>Yeniden dene</button></div>}
            {undoSnap && <div className="po-undo" role="status">{undoSnap.msg} · <button type="button" className="po-link" onClick={undo}>Geri al</button></div>}
          </div>

          {/* C — Taşıma */}
          <div className="po-form-sec"><h4>Taşıma</h4>
            <div className="po-grid4">
              <Field label="Nakliyeci / kargo"><input value={head.carrier} maxLength={100} onChange={(e) => set("carrier", e.target.value)} /></Field>
              <Field label="Plaka" hint={eDespatch ? "sevkte zorunlu" : undefined}>
                <input value={head.vehiclePlate} maxLength={20} placeholder="34ABC123" onChange={(e) => set("vehiclePlate", e.target.value.toUpperCase())} />
                {plateHint && <span className="po-fieldnote warn">{plateHint}</span>}
              </Field>
              <Field label="Dorse plakası" hint="isteğe bağlı">
                <input value={head.trailerPlate} maxLength={20} onChange={(e) => set("trailerPlate", e.target.value.toUpperCase())} />
                {trailerHint && <span className="po-fieldnote warn">{trailerHint}</span>}
              </Field>
              <Field label="Plaka türü" as="div">
                <label className="sh-ed-checkrow"><input type="checkbox" checked={foreign} onChange={(e) => set("plateIsForeign", e.target.checked ? "1" : "")} /> Yabancı plakalı araç</label>
              </Field>
              <Field label="Şoför adı"><input value={head.driverFirstName} maxLength={50} onChange={(e) => set("driverFirstName", e.target.value)} /></Field>
              <Field label="Şoför soyadı"><input value={head.driverLastName} maxLength={50} onChange={(e) => set("driverLastName", e.target.value)} /></Field>
              <Field label="Şoför TCKN" hint="11 hane">
                <input value={head.driverTckn} maxLength={11} inputMode="numeric" className={tried && tcknBad ? "is-invalid" : ""} onChange={(e) => set("driverTckn", e.target.value.replace(/\D/g, ""))} />
                {tcknBad && <span className="po-fielderr">11 haneli olmalı</span>}
              </Field>
              <Field label="Taşıyıcı VKN / TCKN" hint={eDespatch ? "şoför yoksa" : "isteğe bağlı"}>
                <input value={head.carrierTaxNumber} maxLength={11} inputMode="numeric" className={tried && carrierIdBad ? "is-invalid" : ""} onChange={(e) => set("carrierTaxNumber", e.target.value.replace(/\D/g, ""))} />
                {carrierIdBad && <span className="po-fielderr">VKN 10, TCKN 11 hane</span>}
              </Field>
              <Field label="Takip no"><input value={head.trackingNo} maxLength={50} onChange={(e) => set("trackingNo", e.target.value)} /></Field>
              <Field label="Brüt ağırlık" as="div">
                <div className="po-money"><input inputMode="decimal" placeholder="0" aria-label="Brüt ağırlık" value={head.grossWeightKg} className={tried && weightBad ? "is-invalid" : ""} onChange={(e) => set("grossWeightKg", e.target.value)} /><i>kg</i></div>
              </Field>
              {!eDespatch && <>
                <Field label="Resmi irsaliye no" hint="isteğe bağlı"><input value={head.waybillNo} maxLength={32} onChange={(e) => set("waybillNo", e.target.value)} /><span className="po-fieldnote">Sevk ederken de girilebilir</span></Field>
                <Field label="İrsaliye tarihi">
                  <input type="date" value={head.waybillDate} disabled={!head.waybillNo.trim()} onChange={(e) => set("waybillDate", e.target.value)} />
                  {!head.waybillNo.trim() && <span className="po-fieldnote">İrsaliye no girilince etkin</span>}
                </Field>
              </>}
            </div>
            <p className="po-form-hint">{eDespatch
              ? "e-İrsaliye açık: irsaliye numarasını sevkte sistem verir. Sevk için plaka ile şoför (ad, soyad, TCKN) ya da taşıyıcı VKN'si ve teslimat posta kodu gerekir."
              : "Resmi irsaliye numarası bu siparişlerin faturasında irsaliye referansı olarak kullanılır."}</p>
          </div>

          {/* D — Teslimat adresi */}
          <div className="po-form-sec">
            <h4>Teslimat adresi{firm && <button type="button" className="po-btn small" onClick={applyFirmAddress}>Firma kartından al</button>}</h4>
            <div className="po-grid4">
              <Field label="Teslim yeri / unvan" span={2}><input value={head.deliveryTitle} maxLength={200} placeholder={firm?.firmName} onChange={(e) => set("deliveryTitle", e.target.value)} /></Field>
              <Field label="İlgili kişi"><input value={head.deliveryContact} maxLength={100} placeholder={firm?.contactPerson} onChange={(e) => set("deliveryContact", e.target.value)} /></Field>
              <Field label="Telefon"><input value={head.deliveryPhone} maxLength={30} placeholder={firm?.phone} onChange={(e) => set("deliveryPhone", e.target.value)} /></Field>
              <Field label="Adres" span={2}><input value={head.deliveryAddress} maxLength={500} placeholder={firm?.address || "Boş bırakılırsa firma adresi kullanılır"} onChange={(e) => set("deliveryAddress", e.target.value)} /></Field>
              <Field label="İlçe"><input value={head.deliveryDistrict} maxLength={100} placeholder={firm?.district?.districtName} onChange={(e) => set("deliveryDistrict", e.target.value)} /></Field>
              <Field label="İl"><input value={head.deliveryCity} maxLength={100} placeholder={firm?.city?.cityName} onChange={(e) => set("deliveryCity", e.target.value)} /></Field>
              <Field label="Posta kodu" hint={eDespatch ? "sevkte zorunlu" : "5 hane"}>
                <input value={head.deliveryPostalCode} maxLength={5} inputMode="numeric" placeholder={firm?.postalCode || "34200"} className={tried && postalBad ? "is-invalid" : ""} onChange={(e) => set("deliveryPostalCode", e.target.value.replace(/\D/g, ""))} />
                {postalBad ? <span className="po-fielderr">5 haneli olmalı</span> : postalHint && <span className="po-fieldnote warn">{postalHint}</span>}
              </Field>
            </div>
            <p className="po-form-hint">Boş bırakılan alanlar kaydederken firma kartından kopyalanır.</p>
          </div>

          {/* E — Not */}
          <div className="po-form-sec"><h4>Not</h4>
            <label className="po-field">
              <textarea rows={2} maxLength={500} value={head.note} aria-label="Not" placeholder="Teslimat koşulu, alıcıya not…" onChange={(e) => set("note", e.target.value)} />
              <span className="po-counter">{head.note.length}/500</span>
            </label>
          </div>
        </div>

        <div className="po-modal-foot between">
          <div className="po-sum">
            <div><span>Sipariş</span><b>{rowCount}</b></div>
            <div><span>Koli</span><b>{pkTotal || head.packageCount || "—"}</b></div>
            <div className="grand"><span>Toplam</span><b>{totals.join(" · ") || "—"}</b></div>
          </div>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" onClick={requestClose} disabled={!!busy}>Vazgeç</button>
            <button type="button" className={`po-btn ${canShip ? "" : "primary"}`} onClick={() => submit(false)} disabled={!!busy}>{busy === "draft" ? "Kaydediliyor" : shipment ? "Taslağı kaydet" : "Taslak kaydet"}</button>
            {canShip && <button type="button" className="po-btn primary" onClick={() => submit(true)} disabled={!!busy}>{I.send} {busy === "ship" ? "Kaydediliyor" : "Kaydet ve sevk et"}</button>}
          </div>
        </div>
      </form>

      {/* Seçici ve üst katmanlar (OrderPicker .fp-overlay portalıdır; açıkken modal Esc'i bekler) */}
      <OrderPickerModal
        open={pickerOpen}
        onClose={() => { setPickerOpen(false); const applied = justApplied.current; justApplied.current = false; if (!applied) window.setTimeout(() => addBtnRef.current?.focus(), 0); }}
        orders={orders} loading={ordersLoading} error={ordersError} onReload={loadOrders}
        value={added} onApply={applyOrders} firmName={firm?.firmName}
      />

      <ConfirmModal isOpen={pendingFirm !== undefined} type="danger" title="Firma değiştirilsin mi?" message={`Sevkiyata eklenen ${added.length} sipariş bu firmaya ait. Firma değişirse satırlar kaldırılacak.`}
        confirmText="Firmayı değiştir" cancelText="Vazgeç" onConfirm={() => { applyFirm(pendingFirm ?? null); closeFirmChange(); }} onCancel={closeFirmChange} />
      <ConfirmModal isOpen={guardOpen} type="warning" title="Kaydedilmemiş değişiklikler" message="Bu sevkiyattaki değişiklikler kaydedilmedi. Kapatırsanız kaybolur."
        confirmText="Kaydetmeden kapat" cancelText="Düzenlemeye dön" onConfirm={() => { setGuardOpen(false); finish(); }} onCancel={() => setGuardOpen(false)} />
    </div>
  );
};

export default ShipmentModal;
